import { useTranslation } from 'react-i18next';

import { RelatedProductCategoryNameEnum } from '@electricjs/core_entity-client';
import {
  Flex,
  Text,
  Icon,
  IconButton,
  Button,
  Divider,
  ColorsV2,
  useThemeColors,
  spaces,
  Box,
} from '@electricjs/arc';

import { Product } from '@/types/products';
import { StorefrontAsset } from '@/types/assets';
import { Store, StorefrontSteps } from '@/types/storefront';
import formatCurrency from '@/helpers/formatCurrency';
import { useGlobalUI } from '@/components/GlobalUIProvider';

import { useStorefront } from '../StorefrontProvider';
import { getItemRelatedProducts } from '../utils/helpers';
import { ItemImage } from './ItemImage';
import { ItemMiniList } from './ItemMiniList';

type StoreDrawerContentProps = {
  item: Product | StorefrontAsset;
};

export const StoreDrawerContent = ({ item }: StoreDrawerContentProps) => {
  const { t } = useTranslation('storefront');
  const { closeGlobalDrawer } = useGlobalUI();

  const [dividerColor] = useThemeColors([ColorsV2.GRAY]);

  const {
    itemsInCart,
    subTotalPrice,
    orderCurrency,
    catalogCart,
    addCatalogCartEntry,
    removeCatalogCartEntry,
    setStorefrontStep,
  } = useStorefront();

  return (
    <Flex vertical height="100%" rowGap={spaces.space_m_rem}>
      {/* Header */}
      <Flex
        flexShrink={0}
        width="100%"
        justifyContent="space-between"
        vAlignContent="center">
        <Flex vAlignContent="center" columnGap={spaces.space_xs_rem}>
          <Icon icon="check-circle" intent="success" size="medium" />
          <Text variant="heading-3">{t('sections.storeDrawer.title')}</Text>
        </Flex>
        <IconButton
          id="store-drawer-close-button"
          icon="close"
          onClick={closeGlobalDrawer}
          intent="neutral"
          aria-label={t('sections.storeDrawer.close')}
          testId="store-drawer-close-button"
        />
      </Flex>

      <Divider color={dividerColor} />

      {/* Item details */}
      <Flex
        flexShrink={0}
        width="100%"
        vAlignContent="center"
        columnGap={spaces.space_m}>
        {'image' in item && (
          <ItemImage
            flexShrink="0"
            src={item.image}
            alt={item.name}
            size="cartSize" // same size used on cart
          />
        )}
        <Text variant="label-small" title={item.name}>
          {item.name}
        </Text>
      </Flex>

      <Divider color={dividerColor} />

      {/* Body (growing vertically through the bottom) */}
      <Flex vertical flex="1" width="100%" overflow="hidden">
        {/* Recommended products (only catalog products recommended for a product or asset)*/}
        {getItemRelatedProducts(
          item,
          RelatedProductCategoryNameEnum.Accessories
        ).length > 0 && (
          <Flex
            vertical
            flex="1"
            width="100%"
            overflow="hidden"
            rowGap={spaces.space_m_rem}>
            <Flex width="100%" vertical>
              <Text variant="heading-3">
                {t('sections.storeDrawer.relatedItemsTitle')}
              </Text>
              <Text variant="legal">
                {t('sections.storeDrawer.relatedItemsSubtitle', {
                  itemName: item.name,
                })}
              </Text>
            </Flex>
            <Box flex="1" width="100%" overflow="hidden">
              {/* Product list with internal scroll */}
              <ItemMiniList
                id="recommended-products-in-shopping-step"
                store={Store.Catalog}
                items={getItemRelatedProducts(
                  item,
                  RelatedProductCategoryNameEnum.Accessories
                )}
                selectedItemsIds={catalogCart.map(entry => entry.item.id)}
                onAddItem={addCatalogCartEntry}
                onRemoveItem={removeCatalogCartEntry}
              />
            </Box>
          </Flex>
        )}
      </Flex>

      <Divider color={dividerColor} />

      {/* Footer */}
      <Flex vertical flexShrink={0} width="100%" rowGap={spaces.space_m_rem}>
        <Flex
          width="100%"
          justifyContent="space-between"
          vAlignContent="center">
          <Text variant="body-2">
            {t('sections.storeDrawer.subtotal', { count: itemsInCart })}
          </Text>
          <Text variant="label-small">
            {formatCurrency(subTotalPrice, orderCurrency)}
          </Text>
        </Flex>
        <Flex width="100%" columnGap={spaces.space_m_rem}>
          <Button
            id="store-drawer-continue-shopping-button"
            variant="outline"
            fluid
            onClick={closeGlobalDrawer}>
            {t('sections.storeDrawer.continueShopping')}
          </Button>
          <Button
            id="store-drawer-view-cart-button"
            fluid
            onClick={() => {
              setStorefrontStep(StorefrontSteps.CART); // go to cart step
              closeGlobalDrawer();
            }}>
            {t('sections.storeDrawer.viewCart')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
